import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import StudentReportCard from '../components/StudentReportCard';

export default function ParentLookup() {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Function to extract only digits from phone number
  const extractDigits = (phoneNumber) => {
    return phoneNumber.replace(/\D/g, '');
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    // Extract only digits from the input
    const digitsOnly = extractDigits(phoneNumber.trim());
    
    if (digitsOnly.length < 7) {
      setError('Please enter a valid phone number (at least 7 digits)');
      return;
    }

    setLoading(true);
    setError(null);
    setStudents([]);
    setSelectedStudent(null);

    try {
      // Search for students by parent phone numbers using only digits
      const { data, error } = await supabase
        .from('users')
        .select(`
          id,
          name,
          email,
          code,
          parent1_number,
          parent2_number
        `)
        .eq('role', 'student')
        .or(`parent1_number.ilike.%${digitsOnly}%,parent2_number.ilike.%${digitsOnly}%`);

      if (error) throw error;

      if (data && data.length > 0) {
        setStudents(data);
      } else {
        setError('No students found with that parent phone number');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
  };

  const handleBackToSearch = () => {
    setSelectedStudent(null);
  };

  if (selectedStudent) {
    return (
      <div className="elegant-font min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={handleBackToSearch}
                  className="p-2 text-neutral-600 hover:text-neutral-900"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <img 
                  src="/logo.jpeg" 
                  alt="Abubakar Islamic Institute Logo" 
                  className="h-8 sm:h-10 w-auto object-contain"
                />
                <h1 className="text-base sm:text-lg font-bold text-neutral-900">Student Report</h1>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="card p-4 sm:p-6">
            <StudentReportCard student={selectedStudent} />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="elegant-font min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/logo.jpeg" 
                alt="Abubakar Islamic Institute Logo" 
                className="h-8 sm:h-10 w-auto object-contain"
              />
              <h1 className="text-base sm:text-lg md:text-xl font-bold text-neutral-900 truncate">
                Abubakar Islamic Institute
              </h1>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-neutral-600 hover:text-neutral-900 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Home
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="card p-6 sm:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-2">Parent Portal</h2>
            <p className="text-sm sm:text-base text-neutral-600">
              Enter your phone number to view your child's academic progress
            </p>
          </div>

          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-neutral-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter phone number"
                className="input w-full text-base"
                required
              />
              <p className="mt-2 text-xs text-neutral-500">
                Only digits will be used for searching (spaces, dashes, and parentheses will be ignored)
              </p>
              {phoneNumber && (
                <p className="mt-1 text-xs text-primary-600 font-medium">
                  Searching with: {extractDigits(phoneNumber) || 'No digits found'}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 text-base font-semibold"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 loading-spinner mr-2"></div>
                  Searching...
                </>
              ) : (
                'Search for Students'
              )}
            </button>
          </form>

          {error && (
            <div className="mt-4 card p-3 border-l-4 border-error-500 bg-error-50">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-error-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-error-700 font-medium text-sm">{error}</p>
              </div>
            </div>
          )}

          {students.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                Found {students.length} student{students.length > 1 ? 's' : ''}
              </h3>
              <div className="space-y-3">
                {students.map((student) => (
                  <button
                    key={student.id}
                    onClick={() => handleStudentSelect(student)}
                    className="w-full card p-4 hover:bg-neutral-50 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-sm">
                            {student.name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h4 className="text-base font-semibold text-neutral-900">{student.name}</h4>
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
